import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { X } from 'lucide-react';
import { useAudio } from '@chainlit/react-client';
import { activeAudioButtonState } from '@/state/chat';

import RealtimeAudioVisualizer from '@/components/RealtimeAudioVisualizer';

// Signature Aviation Brand Colors
const BRAND_COLORS = {
  runway: '#161D25',      // Dark navy/black
  midnightSky: '#152644', // Deep blue
  landing: '#ECF2FF',     // Light blue/white
  altitude: '#FFFFFF',    // White
  wingspan: '#354646',    // Dark teal
  flightPlan: '#63647D',  // Gray
  jet: '#10DC9E',         // Bright teal/green (accent)
};

interface Props {
  onClose: () => void;
}

/**
 * Full-screen overlay for Realtime Assistant mode.
 * Shows large AI orb visualizer in center with transcript display.
 * Closing the overlay does NOT end the session - it just minimizes to edge animations.
 * User must press Alt+A or click the button to actually end the session.
 */
export default function AIAssistantOverlay({ onClose }: Props) {
  const { audioConnection } = useAudio();
  const activeButton = useRecoilValue(activeAudioButtonState);
  const overlayRef = useRef<HTMLDivElement>(null);

  const isActive = audioConnection === 'on' && activeButton === 'assistant';

  // Handle closing - just closes the overlay, does NOT end the conversation
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Auto-close if connection ends externally
  useEffect(() => {
    if (!isActive) {
      onClose();
    }
  }, [isActive, onClose]);

  // Prevent body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [handleClose]);

  if (!isActive) return null;

  return createPortal(
    <div 
      ref={overlayRef}
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={(e) => {
        // Close if clicking the background
        if (e.target === overlayRef.current) {
          handleClose();
        }
      }}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute top-6 right-6 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200 group"
        aria-label="Close AI Assistant"
      >
        <X className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
      </button>

      {/* Main content area */}
      <div className="relative flex flex-col items-center justify-center gap-8 px-4 max-w-2xl w-full">
        {/* Large AI Orb Visualizer */}
        <div className="relative">
          <RealtimeAudioVisualizer size={200} isActive={true} />
        </div>

        {/* Status Text */}
        <div className="text-center space-y-2">
          <h2 
            className="text-2xl md:text-3xl font-light"
            style={{ color: BRAND_COLORS.landing }}
          >
            AI Assistant
          </h2>
          <p 
            className="text-sm md:text-base animate-pulse"
            style={{ color: BRAND_COLORS.jet }}
          >
            Listening...
          </p>
        </div>

        {/* Hint text */}
        <p 
          className="text-xs text-center max-w-md"
          style={{ color: `${BRAND_COLORS.flightPlan}` }}
        >
          Speak naturally to interact with your AI assistant. Click X or outside to minimize (session continues with edge animations).
        </p>
        
        {/* Keyboard shortcut hint */}
        <div 
          className="flex items-center gap-2 text-xs"
          style={{ color: BRAND_COLORS.flightPlan }}
        >
          <span>Press</span>
          <kbd 
            className="px-2 py-1 rounded text-[10px]"
            style={{ 
              backgroundColor: BRAND_COLORS.wingspan,
              color: BRAND_COLORS.landing 
            }}
          >
            Alt+A
          </kbd>
          <span>to end session</span>
        </div>
      </div>

      {/* Ambient background glow */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: `radial-gradient(circle at center, ${BRAND_COLORS.jet}25 0%, ${BRAND_COLORS.midnightSky}15 40%, transparent 70%)`,
        }}
      />
    </div>,
    document.body
  );
}
